import { supabase } from "@/lib/supabaseClient";
import {
  getPeriodWorkspaceBundle,
  type PeriodWorkspaceBundle,
} from "./payroll/workspaceBundle";
import type {
  BankExportFormat,
  PayrollPeriod,
  PayrollPreflight,
  PayrollReturnReason,
  PayrollRun,
} from "@shared/types/payroll";

import {
  type CompanyPayrollPeriod,
  type PreflightDetails,
  type TimesheetDataset,
  type EmployeeDisputeRow,
  type IssuePayrollRunResponse,
  type IssuePayrollRunBankingMode,
  type IssuerQueueRun,
  type EmployeeProvisioningResult,
  type EmployeeProfileSummary,
  type EmployeeWorkspacePayslipRow,
  type EmployeeSelfServiceTimesheetEntry,
  type EmployeeSelfServiceTimeDispute,
  type EmployeeSelfServiceAbsence,
  type EmployeeSelfServiceLeaveRequest,
  type CompanyEmployeeRosterRow,
  type CompanyTimeAwayQueueRow,
  type ApproveCompanyTimeAwayRequestInput,
  type RejectCompanyTimeAwayRequestInput,
  type PayrollRunReturnItem,
  type EmployeeIssuanceReadinessRow,
  type PendingEmployeeFieldVerificationRow,
  type VerifyPendingEmployeeFieldValueInput,
  type VerifyPendingEmployeeFieldValueResult,
} from "./payroll/mappers";

import type {
  IssuerQueueBundle,
  IssuerReviewDetailBundle,
  IssuedPayrollUiBundle,
} from "./payroll/issuer";

import {
  ensureScheduledPeriodForCompany,
  getPeriodsForCompany,
  createPeriod,
  ensureRun,
  submit,
  returnToClerk,
  issue,
  createBankExport,
  deferOpenTimeDisputeToNextPeriod,
  type PayrollSubmitResult,
} from "./payroll/runLifecycle";

import {
  getIssuerQueueBundle,
  getIssuerQueueRuns,
  getIssuerReviewDetailBundle,
  getIssuedPayrollUiBundle,
  getIssuedPayrollOperationalBundle,
  getIssuedPayrollReplayIntegrityBundle,
  listPayrollRunReturnItems,
  addPayrollRunReturnItem,
  escalateReturnItemToResolver,
  acceptResolutionAssignment,
  cancelResolutionAssignment,
  resolvePayrollRunReturnItem,
  getAuthorizedResolverQueue,
  getIssuedRunsHistory,
  getEmployeeIssuanceReadiness,
  getRunIssuanceReadiness,
  returnRunToClerk,
  issuePayrollRun,
} from "./payroll/issuer";

import {
  getTimesheetRowsForPeriod,
  approveTimesheetRange,
  getEmployeeDisputesForPeriod,
  updateTimeDispute,
  createManualTimeLog,
  bulkCreateManualTimeLogs,
  validateTeamLeaderTimesheetRows,
  correctManualTimeLog,
} from "./payroll/timesheets";

import { preflight } from "./payroll/preflight";

import {
  listMyTimesheetEntries,
  listMyTimeDisputes,
  submitMyTimeDispute,
  cancelMyTimeDispute,
  listMyAbsences,
  reportMyAbsence,
  reportMySickAbsence,
  listMyLeaveRequests,
  requestMyLeave,
} from "./payroll/employeeWorkspace";

import {
  listCompanyEmployeeRoster,
  listCompanyTimeAwayQueue,
  approveCompanyTimeAwayRequest,
  rejectCompanyTimeAwayRequest,
} from "./payroll/timeAway";

import {
  listPendingEmployeeFieldVerifications,
  verifyPendingEmployeeFieldValue,
  verifyPendingEmployeeFieldValues,
} from "./payroll/employeeFieldVerification";

export const payrollApi = {
  /**
   * lifecycle
   */
  getPeriodWorkspaceBundle,
  ensureScheduledPeriodForCompany,
  getPeriodsForCompany,
  createPeriod,
  ensureRun,
  submit,
  returnToClerk,
  issue,
  createBankExport,
  deferOpenTimeDisputeToNextPeriod,

  /**
   * issuer workflow
   */
  getIssuerQueueBundle,
  getIssuerQueueRuns,
  getIssuerReviewDetailBundle,
  getIssuedPayrollUiBundle,
  getIssuedPayrollOperationalBundle,
  getIssuedPayrollReplayIntegrityBundle,
  getIssuedRunsHistory,
  returnRunToClerk,
  issuePayrollRun,
  listPayrollRunReturnItems,
  addPayrollRunReturnItem,
  escalateReturnItemToResolver,
  acceptResolutionAssignment,
  cancelResolutionAssignment,
  resolvePayrollRunReturnItem,
  getAuthorizedResolverQueue,
  getEmployeeIssuanceReadiness,
  getRunIssuanceReadiness,

  /**
   * preflight
   */
  preflight,

  /**
   * timesheets
   */
  getTimesheetRowsForPeriod,
  approveTimesheetRange,
  getEmployeeDisputesForPeriod,
  updateTimeDispute,
  createManualTimeLog,
  bulkCreateManualTimeLogs,
  validateTeamLeaderTimesheetRows,
  correctManualTimeLog,

  /**
   * employee self service
   */
  listMyTimesheetEntries,
  listMyTimeDisputes,
  submitMyTimeDispute,
  cancelMyTimeDispute,
  listMyAbsences,
  reportMyAbsence,
  reportMySickAbsence,
  listMyLeaveRequests,
  requestMyLeave,

  listPayslipsForMe: async () => {
    const { data, error } = await supabase
      .schema("payroll")
      .rpc("get_my_issued_payslip_history");

    if (error) {
      throw error;
    }

    return (data ?? []) as EmployeeWorkspacePayslipRow[];
  },

  listMyPayslips: async (_companyId: string) => {
    const { data, error } = await supabase
      .schema("payroll")
      .rpc("get_my_issued_payslip_history");

    if (error) {
      throw error;
    }

    return (data ?? []) as EmployeeWorkspacePayslipRow[];
  },

  /**
   * time away
   */
  listCompanyEmployeeRoster,
  listCompanyTimeAwayQueue,
  approveCompanyTimeAwayRequest,
  rejectCompanyTimeAwayRequest,

  /**
   * employee field verification
   */
  listPendingEmployeeFieldVerifications,
  verifyPendingEmployeeFieldValue,
  verifyPendingEmployeeFieldValues,

  /**
   * compensation
   */
  getEmployeeCompensationView: async (companyId: string) => {
    return supabase.schema("payroll").rpc(
      "get_safe_employee_compensation_view",
      { p_company_id: companyId }
    );
  },

  mayViewCompensation: async (companyId: string) => {
    return supabase.schema("payroll").rpc(
      "may_view_compensation",
      { p_company_id: companyId }
    );
  },

  mayEditCompensation: async (companyId: string) => {
    return supabase.schema("payroll").rpc(
      "may_edit_compensation",
      { p_company_id: companyId }
    );
  },

  updateEmployeeHourlyRate: async (
    companyId: string,
    employeeId: string,
    hourlyRate: number,
    effectiveFrom: string,
  ) => {
    return supabase.schema("payroll").rpc(
      "upsert_employee_hourly_rate",
      {
        p_company_id: companyId,
        p_employee_id: employeeId,
        p_hourly_rate: hourlyRate,
        p_effective_from: effectiveFrom,
      }
    );
  },

  /**
   * session
   */
  getCurrentUserId: async () => {
    const { data, error } = await supabase.auth.getSession();

    if (error) return { error, userId: null };

    return {
      error: null,
      userId: data.session?.user?.id ?? null,
    };
  },

  upsertEmployeeNumber: async (
    companyId: string,
    employeeId: string,
    customEmployeeId: string | null,
  ) => {
    const { data, error } = await supabase.rpc(
      "upsert_employee_number",
      {
        p_company_id: companyId,
        p_employee_id: employeeId,
        p_custom_employee_id: customEmployeeId,
      }
    );

    return { data, error };
  },

  listOffScheduleInclusions: async (companyId: string, periodId: string) => {
    const { data, error } = await supabase
      .from("employee_off_schedule_period_inclusions")
      .select("*")
      .eq("company_id", companyId)
      .eq("payroll_period_id", periodId)
      .is("revoked_at", null);

    return { data, error };
  },

  addEmployeeToOffSchedulePeriod: async (
    companyId: string,
    periodId: string,
    employeeId: string,
    actorId: string,
    reason: string,
  ) => {
    const { data, error } = await supabase
      .from("employee_off_schedule_period_inclusions")
      .insert({
        company_id: companyId,
        payroll_period_id: periodId,
        employee_id: employeeId,
        included_by_user_id: actorId,
        reason,
      })
      .select("*")
      .single();

    return { data, error };
  },

  revokeOffScheduleInclusion: async (
    inclusionId: string,
    actorId: string,
    revokeReason: string,
  ) => {
    const { data, error } = await supabase
      .from("employee_off_schedule_period_inclusions")
      .update({
        revoked_at: new Date().toISOString(),
        revoked_by_user_id: actorId,
        revoke_reason: revokeReason,
      })
      .eq("id", inclusionId)
      .select("*")
      .single();

    return { data, error };
  },

  /**
   * employee management
   */
  listCompanyEmployees: async (companyId: string) => {
    try {
      const data = await listCompanyEmployeeRoster(companyId);

      return {
        data,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error,
      };
    }
  },

  updateEmployeeActiveStatus: async (
    companyId: string,
    employeeId: string,
    isActive: boolean,
  ) => {
    const { error } = await supabase
      .from("employee_profiles")
      .update({ is_active: isActive })
      .eq("id", employeeId)
      .eq("company_id", companyId);

    return { error };
  },
};

export type {
  BankExportFormat,
  PayrollPeriod,
  PayrollPreflight,
  PayrollReturnReason,
  PayrollRun,
  PeriodWorkspaceBundle,

  CompanyPayrollPeriod,
  PreflightDetails,
  TimesheetDataset,
  EmployeeDisputeRow,

  IssuerQueueBundle,
  IssuerReviewDetailBundle,
  IssuedPayrollUiBundle,

  IssuePayrollRunResponse,
  IssuePayrollRunBankingMode,
  IssuerQueueRun,

  EmployeeProvisioningResult,
  EmployeeProfileSummary,
  EmployeeWorkspacePayslipRow,

  EmployeeSelfServiceTimesheetEntry,
  EmployeeSelfServiceTimeDispute,
  EmployeeSelfServiceAbsence,
  EmployeeSelfServiceLeaveRequest,

  CompanyEmployeeRosterRow,
  CompanyTimeAwayQueueRow,
  ApproveCompanyTimeAwayRequestInput,
  RejectCompanyTimeAwayRequestInput,

  PayrollRunReturnItem,
  EmployeeIssuanceReadinessRow,
  PendingEmployeeFieldVerificationRow,
  VerifyPendingEmployeeFieldValueInput,
  VerifyPendingEmployeeFieldValueResult,
  PayrollSubmitResult,
};